import React, { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormField,
  useAlertContext,
} from '../../../common/form';
import { useFormState } from 'react-hook-form';
import { Padding } from '../../../common/components/Padding';
import { cleanArray, confirmationHeaders } from '../../../common/utils';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutationErrors } from '../../../common/queryClient';
import validationTranslations from '../../translations/validation.translations.json';
import { ActionButton } from '../../../common/components';
import translations from './AlgorithmCloneModal.translations.json';
import { AlgorithmCloneValidFormDataType } from './schema/types';
import { AlgorithmCloneValidFormDataSchema } from './schema/AlgorithmCloneValidFormDataSchema';
import { useMutateAlgorithmCloneQuery } from './query/useMutateAlgorithmCloneQuery';
import { FormDatePicker } from '../../../common/form/input/FormDatePicker';
import { AlgorithmBasicType } from '../schema/types';
import { useListAlgorithmsOverviewQuery } from '../query/useGetAlgorithmsOverviewQuery';
import { AlgorithmCodeAutoComplete } from '../AlgorithmCodeAutoComplete';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';

const { addTranslations, t } = i18nNsFactory('algorithm-clone-modal');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface AlgorithmCloneModalProps {
  algorithmId: number;
  isFinish: boolean;
  onCreate?: (id: number) => void;
  onClose?: () => void;
}

export const AlgorithmCloneModal: FC<AlgorithmCloneModalProps> = (props) => {
  const { algorithmId, isFinish, onCreate, onClose } = props;

  const { mutateAsync } = useMutateAlgorithmCloneQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const handleSubmit = useCallback(
    (values: AlgorithmCloneValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then((response) => {
          onCreate?.((response as AlgorithmBasicType).id);
          setSuccess(t('alert.success.created'));
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      onCreate,
      setSuccess,
      setMutationErrors,
    ],
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const { data: algorithms, isLoading } = useListAlgorithmsOverviewQuery({
    dataObjectVersionId: dataObjectDescriptionVersion.versionId,
  });

  const algorithmCodes = useMemo(
    (): string[] => cleanArray(algorithms?.map(({ code }) => code)) || [],
    [algorithms],
  );

  const algorithmCode = useMemo(
    (): string =>
      algorithms?.find((element) => element?.id === algorithmId)?.code || '',
    [algorithmId, algorithms],
  );

  const defaultValues = useMemo(
    () => ({
      id: algorithmId,
      isFinish,
      cloneValue: algorithmCode,
    }),
    [algorithmId, isFinish, algorithmCode],
  );

  return isLoading ? (
    ''
  ) : (
    <Form
      schema={AlgorithmCloneValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="algorithmMappings"
    >
      <AlgorithmCloneModalInner
        isFinish={isFinish}
        onClose={onClose}
        algorithmCodes={algorithmCodes}
      />
    </Form>
  );
};

function AlgorithmCloneModalInner({
  algorithmCodes,
  isFinish,
  onClose,
}: {
  algorithmCodes: string[];
  isFinish: boolean;
  onClose?: () => void;
}) {
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <ActionButton type="abort" onClick={handleCancel} />
          <ActionButton type="formSubmit" />
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>
          {isFinish ? t(`heading.cloneFinish`) : t(`heading.clone`)}
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding>
        <Flex vertical gap="middle">
          <FormField required name="code" label={t('fields.code.label')}>
            <AlgorithmCodeAutoComplete
              name="code"
              algorithmCodes={algorithmCodes}
            />
          </FormField>
          {isFinish && (
            <FormField
              required
              name="validTo"
              label={t('fields.validTo.label')}
            >
              <FormDatePicker name="validTo" />
            </FormField>
          )}
        </Flex>
      </Padding>
    </Modal>
  );
}
